import type { NftMiniature } from "@anaconda/core/nft/types"
import { action, atom, computed } from "@reatom/core"
import { molecule } from "bunshi"
import { NftsToBuyMoleculeReatom } from "../../nfts-to-buy/molecule.reatom"
import { SweepMoleculeReatom } from "../molecule.reatom"

const MAX_NFT_TO_SWEEP = 25

/**
 * Sweep selector molecule - manages sweep count input and triggers NFT selection
 * Takes available NFTs as parameter to avoid storing large arrays in atoms
 */
export const SweepSelectorMoleculeReatom = molecule((mol) => {
  const { selectedNftsToBuy } = mol(NftsToBuyMoleculeReatom)
  const { selectNftsToSweep, nftsToSweepCount, maxSweep } =
    mol(SweepMoleculeReatom)

  // Local count state for the input/slider
  const localCountAtom = atom<number | undefined>(
    undefined,
    "sweepSelectorCount",
  )

  // Calculate max sweep based on available NFTs count
  const getMaxSweep = (availableNftsCount: number) => {
    return maxSweep()(availableNftsCount)
  }

  // Get display count based on current selection
  const getDisplayCount = (allNfts: NftMiniature[]) => {
    const selectedNfts = selectedNftsToBuy()

    // If no NFTs selected, reset local count and return 0
    if (selectedNfts.length === 0) {
      localCountAtom.set(undefined)
      return undefined
    }

    // Check if current selection is a valid sweep (first N NFTs selected in order)
    const sweepCount = nftsToSweepCount()(selectedNfts, allNfts)

    if (sweepCount > 0) {
      return sweepCount
    }

    // If not a valid sweep, show local input count
    return localCountAtom()
  }

  // Action to set count and select NFTs
  // Takes availableNfts as parameter to avoid storing in atom
  const setCountAction = action(
    (value: number | undefined, availableNfts: NftMiniature[]) => {
      if (value === undefined) {
        localCountAtom.set(0)
        selectNftsToSweep([], 0)
        return
      }

      const max = maxSweep()(availableNfts.length)
      const newValidCount = Math.max(Math.min(value, max), 0)
      localCountAtom.set(newValidCount)

      // Select first N available NFTs
      selectNftsToSweep(availableNfts, newValidCount)
    },
    "setCountAction",
  )

  return {
    getMaxSweep,
    getDisplayCount,
    setCountAction,
  }
})
